import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { submitInspectionRequest } from '../services/api';

const InspectionRequestModal = ({ isOpen, onClose, propertyId }) => {
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    emailAddress: '',
    preferredDate: '',
    preferredTime: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ success: null, message: '' });
  const [fieldErrors, setFieldErrors] = useState({});

  const validatePhoneNumber = (phoneNumber) => {
    // Basic phone number validation for Nigerian format
    const phoneRegex = /^(\+234|0)[789][01]\d{8}$/;
    return phoneRegex.test(phoneNumber.replace(/\s/g, ''));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ success: null, message: '' });

    try {
      // Clear previous field errors
      setFieldErrors({});
      
      // Validate required fields
      const errors = {};
      if (!formData.name.trim()) errors.name = 'Full name is required';
      if (!formData.phoneNumber.trim()) errors.phoneNumber = 'Phone number is required';
      if (!formData.preferredDate) errors.preferredDate = 'Preferred date is required';
      if (!formData.preferredTime) errors.preferredTime = 'Preferred time is required';
      
      // Validate phone number format
      if (formData.phoneNumber.trim() && !validatePhoneNumber(formData.phoneNumber.trim())) {
        errors.phoneNumber = 'Please enter a valid Nigerian phone number (e.g., 08012345678 or +2348012345678)';
      }
      
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        throw new Error('Please fill in all required fields');
      }

      // Validate date is not in the past
      const selectedDate = new Date(formData.preferredDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        setFieldErrors({ preferredDate: 'Please select a future date for inspection' });
        throw new Error('Please select a future date for inspection');
      }

      // Format the date to match API expected format (e.g., "12 Jan 2026")
      const formattedDate = format(selectedDate, 'dd MMM yyyy');
      
      const requestData = {
        ...formData,
        preferredDate: formattedDate
      };

      console.log('Submitting inspection request for property:', propertyId);
      console.log('Request data:', requestData);

      const result = await submitInspectionRequest(requestData, propertyId);
      console.log('Inspection request result:', result);

      setSubmitStatus({
        success: true,
        message: 'Inspection request submitted successfully! We will contact you shortly.'
      });
      
      // Reset form on success
      setFormData({
        name: '',
        phoneNumber: '',
        emailAddress: '',
        preferredDate: '',
        preferredTime: '',
        message: ''
      });
      
      // Clear field errors
      setFieldErrors({});
      
      // Close modal after 3 seconds
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (error) {
      console.error('Error submitting inspection request:', error);
      
      // Provide more specific error messages based on error type
      let errorMessage = 'Failed to submit inspection request. Please try again.';
      
      if (error.message.includes('Authentication failed')) {
        errorMessage = 'Authentication error. Please contact support to verify API configuration.';
      } else if (error.message.includes('endpoint not found')) {
        errorMessage = 'Service temporarily unavailable. Please try again later or contact support.';
      } else if (error.message.includes('Server error')) {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (error.message.includes('API configuration error')) {
        errorMessage = 'System configuration error. Please contact support.';
      } else if (error.message.includes('Unable to connect')) {
        errorMessage = 'Connection error. Please check your internet connection and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSubmitStatus({
        success: false,
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={styles.overlay}>
      <div className="modal-content" style={styles.modal}>
        <button 
          className="modal-close" 
          onClick={onClose}
          style={styles.closeButton}
          disabled={isSubmitting}
        >
          &times;
        </button>
        
        <h2 style={styles.title}>Request Property Inspection</h2>
        
        {submitStatus.message && (
          <div 
            className={`alert ${submitStatus.success ? 'alert-success' : 'alert-danger'}`}
            style={styles.alert}
          >
            {submitStatus.message}
          </div>
        )}
        
        {!submitStatus.success && (
          <form onSubmit={handleSubmit} style={styles.form}>
            <div className="form-group mb-3">
              <label htmlFor="name" className="form-label">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                className={`form-control ${fieldErrors.name ? 'is-invalid' : ''}`}
                value={formData.name}
                onChange={handleChange}
                required
              />
              {fieldErrors.name && (
                <div className="invalid-feedback d-block">
                  {fieldErrors.name}
                </div>
              )}
            </div>
            
            <div className="form-group mb-3">
              <label htmlFor="phoneNumber" className="form-label">Phone Number *</label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                className={`form-control ${fieldErrors.phoneNumber ? 'is-invalid' : ''}`}
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="e.g., 08012345678"
                required
              />
              {fieldErrors.phoneNumber && (
                <div className="invalid-feedback d-block">
                  {fieldErrors.phoneNumber}
                </div>
              )}
            </div>
            
            <div className="form-group mb-3">
              <label htmlFor="emailAddress" className="form-label">Email Address</label>
              <input
                type="email"
                id="emailAddress"
                name="emailAddress"
                className="form-control"
                value={formData.emailAddress}
                onChange={handleChange}
              />
            </div>
            
            <div className="row">
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label htmlFor="preferredDate" className="form-label">Preferred Date *</label>
                  <input
                    type="date"
                    id="preferredDate"
                    name="preferredDate"
                    className={`form-control ${fieldErrors.preferredDate ? 'is-invalid' : ''}`}
                    value={formData.preferredDate}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                  {/* <small className="form-text text-muted">
                    Please select a future date for inspection
                  </small> */}
                  {fieldErrors.preferredDate && (
                    <div className="invalid-feedback d-block">
                      {fieldErrors.preferredDate}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label htmlFor="preferredTime" className="form-label">Preferred Time *</label>
                  <select
                    id="preferredTime"
                    name="preferredTime"
                    className={`form-select ${fieldErrors.preferredTime ? 'is-invalid' : ''}`}
                    value={formData.preferredTime}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Time</option>
                    <option value="09:00AM">09:00 AM</option>
                    <option value="10:00AM">10:00 AM</option>
                    <option value="11:00AM">11:00 AM</option>
                    <option value="12:00PM">12:00 PM</option>
                    <option value="01:00PM">01:00 PM</option>
                    <option value="02:00PM">02:00 PM</option>
                    <option value="03:00PM">03:00 PM</option>
                    <option value="04:00PM">04:00 PM</option>
                  </select>
                  {fieldErrors.preferredTime && (
                    <div className="invalid-feedback d-block">
                      {fieldErrors.preferredTime}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="form-group mb-4">
              <label htmlFor="message" className="form-label">Additional Notes</label>
              <textarea
                id="message"
                name="message"
                className="form-control"
                rows="3"
                value={formData.message}
                onChange={handleChange}
              ></textarea>
            </div>
            
            <div className="d-flex gap-3 mt-3">
              <button
                type="button"
                className="btn-inspection flex-grow-1"
                onClick={onClose}
                disabled={isSubmitting}
                style={{
                  padding: '12px 20px',
                  borderRadius: '4px',
                  fontWeight: '600',
                  backgroundColor: 'white',
                  color: '#ff5a3c',
                  border: '1px solid #ff5a3c',
                  transition: 'all 0.3s ease',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  opacity: isSubmitting ? 0.7 : 1,
                  flex: 1
                }}
                onMouseOver={(e) => !isSubmitting && (e.currentTarget.style.transform = 'translateY(-2px)')}
                onMouseOut={(e) => !isSubmitting && (e.currentTarget.style.transform = 'translateY(0)')}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-reservation flex-grow-1"
                disabled={isSubmitting}
                style={{
                  padding: '12px 20px',
                  borderRadius: '4px',
                  fontWeight: '600',
                  backgroundColor: isSubmitting ? '#cccccc' : '#ff5a3c',
                  color: 'white',
                  border: 'none',
                  transition: 'all 0.3s ease',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  flex: 1
                }}
                onMouseOver={(e) => !isSubmitting && (e.currentTarget.style.transform = 'translateY(-2px)')}
                onMouseOut={(e) => !isSubmitting && (e.currentTarget.style.transform = 'translateY(0)')}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '30px',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: '15px',
    right: '20px',
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '0 10px',
  },
  title: {
    marginBottom: '20px',
    color: '#333',
    fontSize: '24px',
  },
  form: {
    marginTop: '15px',
  },
  alert: {
    marginBottom: '20px',
    padding: '10px 15px',
    borderRadius: '4px',
  },
  invalidField: {
    borderColor: '#dc3545',
    boxShadow: '0 0 0 0.2rem rgba(220, 53, 69, 0.25)',
  },
};

export default InspectionRequestModal;
